import { ContentBlock, StreamingBlockMeta, StreamingEvent } from '../models/Message';

// A block during active streaming: the standard ContentBlock fields plus transient metadata
export type LiveBlock = (ContentBlock & StreamingBlockMeta) | ({ type: string } & StreamingBlockMeta & Record<string, any>);

export interface StreamingState {
    blocks: LiveBlock[]; // sparse array indexed by blockIndex — never reindexed
    isDone: boolean;
    error: string | null;
}

export const initialStreamingState: StreamingState = {
    blocks: [],
    isDone: false,
    error: null,
};

/**
 * Pure reducer that applies a single streaming event to the current state.
 * Key invariant: blocks array indices are never shifted — blockIndex is stable
 * for the lifetime of the stream. Removed blocks are tombstoned in place.
 */
export function applyStreamingEvent(state: StreamingState, event: StreamingEvent): StreamingState {
    switch (event.type) {
        case 'block_start': {
            const blocks = [...state.blocks];
            blocks[event.blockIndex] = {
                type: event.blockType,
                _streaming: true,
                // Pre-initialize text content + per-block seq tracking so block_chunk can buffer safely
                ...(event.blockType === 'text' ? { content: '', _nextSeq: 1, _pendingChunks: {} } : {}),
            } as LiveBlock;
            return { ...state, blocks };
        }

        case 'block_chunk': {
            const blocks = [...state.blocks];
            const b = blocks[event.blockIndex] as any;
            if (b && typeof b.content === 'string') {
                // Buffer the incoming chunk, then flush any consecutive run from _nextSeq
                const pending: Record<number, string> = { ...b._pendingChunks, [event.seq]: event.content };
                let nextSeq: number = b._nextSeq ?? 1;
                let content: string = b.content;

                while (pending[nextSeq] !== undefined) {
                    content += pending[nextSeq];
                    delete pending[nextSeq];
                    nextSeq++;
                }

                blocks[event.blockIndex] = { ...b, content, _nextSeq: nextSeq, _pendingChunks: pending };
            }
            return { ...state, blocks };
        }

        case 'block_end': {
            const blocks = [...state.blocks];
            if (blocks[event.blockIndex]) {
                blocks[event.blockIndex] = { ...blocks[event.blockIndex], _streaming: false };
            }
            return { ...state, blocks };
        }

        case 'block_data': {
            // Atomic block: the full block content arrives in one shot
            const { type: _t, blockIndex, ...rest } = event;
            const blocks = [...state.blocks];
            blocks[blockIndex] = {
                ...rest,
                type: rest.blockType ?? blocks[blockIndex]?.type ?? 'text',
                _streaming: false,
            } as LiveBlock;
            return { ...state, blocks };
        }

        case 'block_remove': {
            const blocks = [...state.blocks];
            if (blocks[event.blockIndex]) {
                blocks[event.blockIndex] = { ...blocks[event.blockIndex], _removed: true };
            }
            return { ...state, blocks };
        }

        case 'block_replace': {
            const { type: _t, blockIndex, blockType, ...rest } = event;
            const blocks = [...state.blocks];
            blocks[blockIndex] = { type: blockType, ...rest, _streaming: false } as LiveBlock;
            return { ...state, blocks };
        }

        case 'done':
            return { ...state, isDone: true };

        case 'error':
            return { ...state, isDone: true, error: event.message };

        default:
            return state;
    }
}
